import type { StaffImportReviewRow } from "@lafayette/schemas";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";

import { hintText } from "../../../catalog/components/styles";
import { itemTypeLabels } from "../../../catalog/labels";
import { AreaLink } from "../../../navigation/AreaLink";
import { deepLinks } from "../../../navigation/deep-links";
import { Button } from "../../../ui/button";
import { DataTable } from "../../../ui/data-table";
import { TableLink } from "../../../ui/links";
import { emptyValue } from "../../components/styles";
import { matchBasisLabels, plural, processingErrorMessages } from "../labels";
import { IssueList, OutcomeBadge, ProcessingBadge } from "./parts";

type RowTableProps = Readonly<{
  rows: readonly StaffImportReviewRow[];
  /** `processing`: show what happened to each row after approval, and where the item is. */
  mode: "review" | "processing";
  selectedRowId: string | null;
  onOpen: (rowId: string, opener: HTMLButtonElement) => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}>;

const rowLabel = (row: StaffImportReviewRow): number => row.sourceRowNumber ?? row.rowNumber;

/** A row of the table with what the "Open" button needs. The column list is built once per mode, so the button keeps its focus. */
type TableRow = Readonly<{
  row: StaffImportReviewRow;
  selected: boolean;
  onOpen: (opener: HTMLButtonElement) => void;
}>;

const column = createColumnHelper<TableRow>();

const openColumn = column.display({
  id: "open",
  header: "Open",
  meta: { label: "Open", headClassName: "w-px" },
  cell: (info) => {
    const { row, selected, onOpen } = info.row.original;
    return (
      <Button
        type="button"
        size="sm"
        variant={selected ? "default" : "secondary"}
        className="-my-2 compact:-my-1"
        aria-label={`Open row ${rowLabel(row)}`}
        aria-expanded={selected}
        onClick={(event) => onOpen(event.currentTarget)}
      >
        Open
      </Button>
    );
  },
});

const sharedStart = [
  column.display({
    id: "row",
    header: "Row",
    meta: { rowHeader: true, className: "font-semibold" },
    cell: (info) => rowLabel(info.row.original.row),
  }),
  column.display({
    id: "name",
    header: "Name",
    meta: { className: "min-w-30 max-w-56 break-words whitespace-pre-wrap" },
    cell: (info) => {
      const { name } = info.row.original.row;
      return name === null ? (
        <span className={emptyValue}>No name</span>
      ) : (
        <span className="font-serif text-[17px] font-medium">{name}</span>
      );
    },
  }),
  column.display({
    id: "type",
    header: "Type",
    cell: (info) => {
      const { itemType } = info.row.original.row;
      return itemType === null ? (
        <span className={emptyValue}>Not set</span>
      ) : (
        itemTypeLabels[itemType]
      );
    },
  }),
  column.display({
    id: "status",
    header: "Status",
    cell: (info) => <OutcomeBadge outcome={info.row.original.row.outcome} />,
  }),
  openColumn,
];

const reviewColumns = [
  ...sharedStart,
  column.display({
    id: "issues",
    header: "Issues",
    meta: { className: "min-w-30 max-w-56 break-words" },
    cell: (info) => <IssueList issues={info.row.original.row.issues} />,
  }),
  column.display({
    id: "match",
    header: "Match",
    cell: (info) => {
      const { row } = info.row.original;
      return row.matchBasis === null ? (
        <span className={emptyValue}>None</span>
      ) : (
        <>
          {matchBasisLabels[row.matchBasis]}
          <br />
          <span className={hintText}>
            {plural(row.candidateCount, "existing item", "existing items")}
          </span>
        </>
      );
    },
  }),
  column.display({
    id: "decisions",
    header: "Decisions needed",
    cell: (info) => {
      const { row } = info.row.original;
      return row.openDecisionCount > 0 ? (
        <strong>
          <span aria-hidden="true">⚠ </span>
          {row.openDecisionCount} open
        </strong>
      ) : (
        <span className={emptyValue}>None</span>
      );
    },
  }),
] as ColumnDef<TableRow, unknown>[];

const processingColumns = [
  ...sharedStart,
  column.display({
    id: "result",
    header: "Result",
    meta: { className: "min-w-30 max-w-56 break-words" },
    cell: (info) => {
      const { row } = info.row.original;
      if (row.processing === null) return <span className={emptyValue}>Not processed</span>;
      return (
        <>
          <ProcessingBadge state={row.processing.state} />
          {row.processing.errorCode === null ? null : (
            <p className="mt-1 [overflow-wrap:anywhere]">
              {processingErrorMessages[row.processing.errorCode]}
            </p>
          )}
          {row.processing.catalogItemId === null ? null : (
            <p className="mt-1 [overflow-wrap:anywhere]">
              <AreaLink
                area="catalog"
                as={TableLink}
                to={deepLinks.catalogItem(row.processing.catalogItemId)}
              >
                Open item
                <span className="sr-only"> of row {rowLabel(row)}</span>
              </AreaLink>
            </p>
          )}
        </>
      );
    },
  }),
] as ColumnDef<TableRow, unknown>[];

/**
 * The rows of a job, 30 at a time (keyset paging). The name is untrusted text from the file: it is shown
 * as text only. The table scrolls inside its own box, so a narrow screen never scrolls the page.
 */
export function RowTable({
  rows,
  mode,
  selectedRowId,
  onOpen,
  hasMore,
  loadingMore,
  onLoadMore,
}: RowTableProps) {
  const data: TableRow[] = rows.map((row) => ({
    row,
    selected: row.id === selectedRowId,
    onOpen: (opener) => onOpen(row.id, opener),
  }));
  return (
    <div className="grid min-w-0 gap-4">
      <DataTable
        banded
        columns={mode === "review" ? reviewColumns : processingColumns}
        data={data}
        getRowId={(entry) => entry.row.id}
        caption={
          mode === "review"
            ? "Rows with their status, issues and the decisions they need"
            : "Rows with what happened to each one"
        }
      />
      <div className="flex flex-wrap items-center gap-4">
        <p role="status" className={hintText}>
          Showing {plural(rows.length, "row", "rows")}
          {hasMore ? ". More are available." : "."}
        </p>
        {hasMore ? (
          <Button type="button" variant="secondary" onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? "Loading…" : "Load more rows"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
